package com.iptvplayer.shared.presentation.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.iptvplayer.shared.data.local.AppPreferences
import com.iptvplayer.shared.domain.model.ContentType
import com.iptvplayer.shared.domain.repository.ContentRepository
import com.iptvplayer.shared.domain.repository.FavoriteRepository
import com.iptvplayer.shared.domain.repository.WatchHistoryRepository
import com.iptvplayer.shared.presentation.state.MediaTrack
import com.iptvplayer.shared.presentation.state.PlayerUiState
import com.iptvplayer.shared.util.Constants
import com.iptvplayer.shared.util.Result
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch

class PlayerViewModel(
    private val favoriteRepository: FavoriteRepository,
    private val watchHistoryRepository: WatchHistoryRepository,
    private val contentRepository: ContentRepository
) : ViewModel() {

    private val _state = MutableStateFlow(PlayerUiState())
    val state: StateFlow<PlayerUiState> = _state.asStateFlow()

    private var historyUpdateJob: Job? = null
    private var contentId: String = ""
    private var contentType: ContentType = ContentType.CHANNEL
    private var contentLogoUrl: String? = null

    /** Series context of the playing episode — enables auto-advance to the next one. */
    private var seriesSourceId: String? = null
    private var seriesId: Int? = null

    /** Guards against the platform firing "ended" more than once per episode. */
    private var advancingToNext = false

    /** Last position actually persisted — used to skip redundant writes. */
    private var lastSavedPositionMs: Long = -1L

    fun initPlayer(
        contentId: String,
        contentName: String,
        streamUrl: String,
        contentType: ContentType,
        contentLogoUrl: String? = null,
        resumePositionMs: Long = 0,
        seriesSourceId: String? = null,
        seriesId: Int? = null
    ) {
        this.contentId = contentId
        this.contentType = contentType
        this.contentLogoUrl = contentLogoUrl
        this.seriesSourceId = seriesSourceId
        this.seriesId = seriesId
        this.advancingToNext = false
        this.lastSavedPositionMs = -1L

        _state.update {
            it.copy(
                contentName = contentName,
                contentLogoUrl = contentLogoUrl,
                streamUrl = streamUrl,
                currentPositionMs = resumePositionMs,
                durationMs = 0,
                isPlaying = true, // autoplay
                isLiveChannel = contentType == ContentType.CHANNEL,
                error = null,
                audioTracks = emptyList(),
                textTracks = emptyList(),
                videoTracks = emptyList(),
                selectedAudioId = null,
                selectedTextId = null,
                selectedVideoId = null,
                playbackCompleted = false,
                isSeriesEpisode = seriesSourceId != null && seriesId != null &&
                    contentType == ContentType.VOD
            )
        }

        viewModelScope.launch {
            val isFav = favoriteRepository.isFavorite(contentId)
            _state.update { it.copy(isFavorite = isFav) }
        }

        // History rows written before series context existed have none — recover
        // it from the episode title ("<Series> · SxxEyy …") so the next-episode
        // button and auto-advance work for old Continue Watching entries too.
        // The recovered context is persisted with the next progress save.
        if (seriesSourceId == null && contentType == ContentType.VOD &&
            contentName.contains(EPISODE_TITLE_MARKER)
        ) {
            val seriesName = contentName.substringBefore(" · ").trim()
            if (seriesName.isNotEmpty()) {
                viewModelScope.launch {
                    val match = contentRepository.pageSeries(null, seriesName, 2, 0)
                    val series = match.firstOrNull { it.name.equals(seriesName, ignoreCase = true) }
                        ?: match.singleOrNull()
                    if (series != null && this@PlayerViewModel.contentId == contentId) {
                        this@PlayerViewModel.seriesSourceId = series.sourceId
                        this@PlayerViewModel.seriesId = series.seriesId
                        _state.update { it.copy(isSeriesEpisode = true) }
                    }
                }
            }
        }

        // Get resume position from history (user preference can disable this)
        if (resumePositionMs == 0L && AppPreferences.resumePlayback.value) {
            viewModelScope.launch {
                val position = watchHistoryRepository.getResumePosition(contentId)
                if (position != null && position > 0) {
                    _state.update { it.copy(currentPositionMs = position) }
                }
            }
        }

        startHistoryUpdates()
    }

    private fun startHistoryUpdates() {
        historyUpdateJob?.cancel()
        historyUpdateJob = viewModelScope.launch {
            // First save fires quickly so the item appears in Continue Watching
            // even if the user exits within the periodic-save window.
            delay(3_000)
            saveProgress()
            while (isActive) {
                delay(Constants.WATCH_HISTORY_UPDATE_INTERVAL_MS)
                saveProgress()
            }
        }
    }

    /**
     * History write rules:
     * - Live channels: position/duration are meaningless — store 0/null so the
     *   UI shows "Live" instead of bogus progress, and resume joins the live edge.
     * - VOD: clamp position into [0, duration] and skip the write entirely when
     *   the position hasn't moved since the last save (e.g. paused) to avoid
     *   pointless backend chatter.
     */
    private suspend fun saveProgress() {
        val currentState = _state.value
        if (currentState.streamUrl.isEmpty()) return
        val isLive = contentType == ContentType.CHANNEL
        val duration = currentState.durationMs.takeIf { it > 0 }
        val position = if (isLive) 0L
            else currentState.currentPositionMs.coerceIn(0L, duration ?: Long.MAX_VALUE)
        if (!isLive && position == lastSavedPositionMs) return
        lastSavedPositionMs = position
        watchHistoryRepository.updateProgress(
            contentId = contentId,
            contentType = contentType,
            contentName = currentState.contentName,
            contentLogoUrl = contentLogoUrl,
            streamUrl = currentState.streamUrl,
            resumePositionMs = position,
            durationMs = if (isLive) null else duration,
            seriesSourceId = seriesSourceId,
            seriesId = seriesId
        )
    }

    /**
     * Fire-and-forget save on a scope that outlives the ViewModel / screen.
     * Used on pause and on stop so the row appears in Continue Watching
     * immediately, without waiting for the periodic timer.
     */
    private fun saveProgressNow(positionOverrideMs: Long? = null) {
        val snapshot = _state.value
        if (snapshot.streamUrl.isEmpty()) return
        val cId = contentId
        val cType = contentType
        val cLogo = contentLogoUrl
        val cName = snapshot.contentName
        val sUrl = snapshot.streamUrl
        val cSeriesSource = seriesSourceId
        val cSeriesId = seriesId
        val isLive = cType == ContentType.CHANNEL
        val dur = if (isLive) null else snapshot.durationMs.takeIf { it > 0 }
        val pos = if (isLive) 0L
            else (positionOverrideMs ?: snapshot.currentPositionMs).coerceIn(0L, dur ?: Long.MAX_VALUE)
        lastSavedPositionMs = pos
        persistScope.launch {
            runCatching {
                watchHistoryRepository.updateProgress(
                    contentId = cId, contentType = cType, contentName = cName,
                    contentLogoUrl = cLogo, streamUrl = sUrl,
                    resumePositionMs = pos, durationMs = dur,
                    seriesSourceId = cSeriesSource, seriesId = cSeriesId,
                )
            }
        }
    }

    fun updatePlaybackState(isPlaying: Boolean, positionMs: Long, durationMs: Long) {
        val wasPlaying = _state.value.isPlaying
        _state.update {
            it.copy(
                isPlaying = isPlaying,
                currentPositionMs = positionMs,
                durationMs = durationMs
            )
        }
        // Save the moment the user pauses so the row appears in
        // Continue Watching without waiting for the periodic timer.
        if (wasPlaying && !isPlaying) saveProgressNow()
    }

    fun updateBufferingState(isBuffering: Boolean) {
        _state.update { it.copy(isBuffering = isBuffering) }
    }

    fun togglePlayPause() {
        _state.update { it.copy(isPlaying = !it.isPlaying) }
    }

    fun seekTo(positionMs: Long) {
        _state.update { it.copy(currentPositionMs = positionMs) }
    }

    fun seekRelative(offsetMs: Long) {
        _state.update {
            val newPos = (it.currentPositionMs + offsetMs).coerceIn(0, it.durationMs.coerceAtLeast(0))
            it.copy(currentPositionMs = newPos)
        }
    }

    fun toggleFavorite() {
        viewModelScope.launch {
            val currentState = _state.value
            if (currentState.isFavorite) {
                favoriteRepository.removeFavorite(contentId)
            } else {
                favoriteRepository.addFavorite(
                    contentId = contentId,
                    contentType = contentType,
                    contentName = currentState.contentName,
                    contentLogoUrl = contentLogoUrl,
                    streamUrl = currentState.streamUrl
                )
            }
            _state.update { it.copy(isFavorite = !it.isFavorite) }
        }
    }

    /**
     * Switch to the previous/next live channel (direction -1 / +1).
     * Channels in the same group come first in zap order; falls back to the
     * full channel list when the current channel has no group siblings.
     */
    fun zapChannel(direction: Int) {
        if (contentType != ContentType.CHANNEL) return
        viewModelScope.launch {
            // Single indexed lookup (or an in-RAM scan on web) — never
            // materializes the whole channel catalog.
            val next = contentRepository.nextChannel(contentId, direction) ?: return@launch
            saveProgressNow()
            contentId = next.id
            contentLogoUrl = next.logoUrl
            lastSavedPositionMs = -1L
            _state.update {
                it.copy(
                    contentName = next.name,
                    contentLogoUrl = next.logoUrl,
                    streamUrl = next.streamUrl,
                    currentPositionMs = 0,
                    durationMs = 0,
                    isPlaying = true,
                    error = null,
                    audioTracks = emptyList(),
                    textTracks = emptyList(),
                    videoTracks = emptyList(),
                    selectedAudioId = null,
                    selectedTextId = null,
                    selectedVideoId = null
                )
            }
            val isFav = favoriteRepository.isFavorite(next.id)
            _state.update { it.copy(isFavorite = isFav) }
        }
    }

    /**
     * Apply a Watch Together sync snapshot from the server: force play/pause
     * and (optionally) hard-seek. Pass [positionMs] = null to leave the local
     * position alone (used when drift is within tolerance, and for live).
     */
    fun applyRemoteState(playing: Boolean, positionMs: Long?) {
        _state.update {
            it.copy(
                isPlaying = playing,
                currentPositionMs = positionMs ?: it.currentPositionMs
            )
        }
    }

    /** Cycle 0 fit → 1 zoom → 2 stretch → 0 … */
    fun cycleResizeMode() {
        _state.update { it.copy(resizeMode = (it.resizeMode + 1) % 3) }
    }

    fun onTracksChanged(audio: List<MediaTrack>, text: List<MediaTrack>) {
        _state.update { it.copy(audioTracks = audio, textTracks = text) }
    }

    /** Quality variants reported by the platform player (ABR streams). */
    fun onVideoTracksChanged(video: List<MediaTrack>) {
        _state.update { it.copy(videoTracks = video) }
    }

    fun selectAudioTrack(id: String?) {
        _state.update { it.copy(selectedAudioId = id) }
    }

    fun selectTextTrack(id: String?) {
        _state.update { it.copy(selectedTextId = id) }
    }

    /** Pin a video quality; null returns to Auto (adaptive). */
    fun selectVideoTrack(id: String?) {
        _state.update { it.copy(selectedVideoId = id) }
    }

    /**
     * The platform player reached the end of a VOD stream.
     *
     * - Series episode → save it as finished and auto-play the next episode in
     *   place (next in season, else first of the next season). The screen never
     *   closes between episodes.
     * - Movie / last episode → mark [PlayerUiState.playbackCompleted] so the
     *   screen can close gracefully.
     */
    fun onPlaybackEnded() {
        if (contentType == ContentType.CHANNEL) return
        if (advancingToNext) return
        advancingToNext = true
        // Mark finished at full duration so Continue Watching offers a restart
        // (resume >95% → position 0) instead of resurrecting a finished episode.
        saveProgressNow(positionOverrideMs = _state.value.durationMs.takeIf { it > 0 })

        if (seriesSourceId == null || seriesId == null || !AppPreferences.autoPlayNextEpisode.value) {
            _state.update { it.copy(playbackCompleted = true, isPlaying = false) }
            return
        }
        advanceToNextEpisode(onNoNext = {
            _state.update { it.copy(playbackCompleted = true, isPlaying = false) }
        })
    }

    /**
     * Manual "Next episode" (button in the player controls). Marks the current
     * episode watched and jumps to the next one; does nothing on the last
     * episode (the button case shouldn't close the screen under the user).
     */
    fun playNextEpisode() {
        if (contentType == ContentType.CHANNEL) return
        if (seriesSourceId == null || seriesId == null) return
        if (advancingToNext) return
        advancingToNext = true
        saveProgressNow(positionOverrideMs = _state.value.durationMs.takeIf { it > 0 })
        advanceToNextEpisode(onNoNext = { advancingToNext = false })
    }

    private fun advanceToNextEpisode(onNoNext: () -> Unit) {
        val srcId = seriesSourceId ?: return onNoNext()
        val serId = seriesId ?: return onNoNext()
        viewModelScope.launch {
            val info = (contentRepository.loadSeriesInfo(srcId, serId) as? Result.Success)?.data
            val episodes = info?.seasons
                ?.sortedBy { it.seasonNumber }
                ?.flatMap { season -> season.episodes.sortedBy { it.episodeNumber } }
                .orEmpty()
            val currentIdx = episodes.indexOfFirst { it.id == contentId }
            val next = if (currentIdx >= 0) episodes.getOrNull(currentIdx + 1) else null
            if (next == null || info == null) {
                onNoNext()
                return@launch
            }
            val label = "S${next.seasonNumber.toString().padStart(2, '0')}" +
                "E${next.episodeNumber.toString().padStart(2, '0')}  ${next.title}"
            initPlayer(
                contentId = next.id,
                contentName = "${info.series.name} · $label",
                streamUrl = next.streamUrl,
                contentType = ContentType.VOD,
                contentLogoUrl = next.coverUrl ?: info.series.posterUrl,
                seriesSourceId = srcId,
                seriesId = serId
            )
            _state.update { it.copy(upNext = label) }
        }
    }

    /** Hide the "Up next" toast (called by the UI a few seconds after it appears). */
    fun clearUpNext() {
        _state.update { it.copy(upNext = null) }
    }

    /** Clear the error and ask the platform player to re-prepare. */
    fun retry() {
        _state.update {
            it.copy(error = null, isPlaying = true, retryToken = it.retryToken + 1)
        }
    }

    fun stop() {
        historyUpdateJob?.cancel()
        saveProgressNow()
        _state.update { it.copy(isPlaying = false) }
    }

    fun onError(message: String) {
        _state.update { it.copy(error = message, isPlaying = false) }
    }

    override fun onCleared() {
        historyUpdateJob?.cancel()
        super.onCleared()
    }

    private companion object {
        /** Episode titles are always formatted "<Series> · SxxEyy …". */
        val EPISODE_TITLE_MARKER = Regex(""" · S\d""")

        /**
         * Outlives any single screen so final progress writes survive
         * navigation. Supervised (one failed write can't kill the scope) and
         * every launch wraps its body in runCatching — unlike the old
         * GlobalScope usage this can't crash the app or pile up unsupervised.
         */
        val persistScope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    }
}
